import React, { useState } from "react";
import InvoiceTable from "../components/Inovice/InvoiceTable";
import { makeRequest } from "./Api";
import ErrorMessage from "../components/error/Error";

// Main Invoice Page Component
const InvoicePage = () => {
  const [invoiceNo, setInvoiceNo] = useState(""); // Single input value
  const [invoices, setInvoices] = useState([]); // List of invoices
  const [data, setData] = useState([]); // Fetched data
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error state
  const [validationError, setValidationError] = useState(null); // Validation error for max invoices

  const handleAddInvoice = () => {
    if (invoices?.length >= 10) {
      setValidationError("You can only add up to 10 invoices.");
      return;
    }
    if (invoiceNo.trim() === "") {
      setValidationError("Invoice number cannot be empty.");
      return;
    }
    setInvoices([...invoices, invoiceNo.trim()]);
    setInvoiceNo(""); // Clear input after adding
    setValidationError(null); // Reset validation error
  };

  const handleRemoveInvoice = (index) => {
    const newInvoices = [...invoices];
    newInvoices.splice(index, 1);
    setInvoices(newInvoices);
  };

 const fetchDetails = async () => {
  setLoading(true);
  setError(null);
  setData([]); // Clear previous data
  const results = [];
  const errorMessages = []; // Store error messages

  try {
    for (const invoice of invoices) {
      try {
        const result = await makeRequest(invoice);

        // Parse the SOAP response
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(result, "text/xml");

        // Extract E_HEAD fields
        const headNode = xmlDoc.querySelector("E_HEAD");
        const head = headNode
          ? {
              VBELN: headNode.querySelector("VBELN")?.textContent || "",
              FKDAT: headNode.querySelector("FKDAT")?.textContent || "",
              BSTNK_VF: headNode.querySelector("BSTNK_VF")?.textContent || "",
              VGBEL: headNode.querySelector("VGBEL")?.textContent || "",
              NETWR: headNode.querySelector("NETWR")?.textContent || "",
              MWSBK: headNode.querySelector("MWSBK")?.textContent || "",
              ZADV: headNode.querySelector("ZADV")?.textContent || "",
              ZRET: headNode.querySelector("ZRET")?.textContent || "",
              ZRVAT: headNode.querySelector("ZRVAT")?.textContent || "",
              ZBAMT: headNode.querySelector("ZBAMT")?.textContent || "",
              ZAVAT: headNode.querySelector("ZAVAT")?.textContent || "",
              ZBTAMT: headNode.querySelector("ZBTAMT")?.textContent || "",
              ZFAMT: headNode.querySelector("ZFAMT")?.textContent || "",
              TEXT1: headNode.querySelector("TEXT1")?.textContent || "",
              ENAME: headNode.querySelector("ENAME")?.textContent || "",
              ZBP: headNode.querySelector("ZBP")?.textContent || "",
              ZSP: headNode.querySelector("ZSP")?.textContent || "",
              ZBPTRN: headNode.querySelector("ZBPTRN")?.textContent || "",
              ZSPTRN: headNode.querySelector("ZSPTRN")?.textContent || "",
              ZCTRN: headNode.querySelector("ZCTRN")?.textContent || "",
              NAME1: headNode.querySelector("NAME1")?.textContent || "",
              CITY1: headNode.querySelector("CITY1")?.textContent || "",
              POST_CODE1: headNode.querySelector("POST_CODE1")?.textContent || "",
              STREET: headNode.querySelector("STREET")?.textContent || "",
              HOUSE_NUM1: headNode.querySelector("HOUSE_NUM1")?.textContent || "",
              LANDX50: headNode.querySelector("LANDX50")?.textContent || "",
              ZNAME1: headNode.querySelector("ZNAME1")?.textContent || "",
              ZCITY1: headNode.querySelector("ZCITY1")?.textContent || "",
              ZPOST_CODE1: headNode.querySelector("ZPOST_CODE1")?.textContent || "",
              ZSTREET: headNode.querySelector("ZSTREET")?.textContent || "",
              ZHOUSE_NUM1: headNode.querySelector("ZHOUSE_NUM1")?.textContent || "",
              ZLANDX50: headNode.querySelector("ZLANDX50")?.textContent || "",
            }
          : {};

        // Extract E_ITEM fields
        const itemNodes = Array.from(xmlDoc.querySelectorAll("E_ITEM > item"));
        const items = itemNodes.slice(1).map(node => ({
          ZSNO: node.querySelector("ZSNO")?.textContent || "",
          MATNR: node.querySelector("MATNR")?.textContent || "",
          ARKTX: node.querySelector("ARKTX")?.textContent || "",
          FKIMG: parseFloat(node.querySelector("FKIMG")?.textContent || "0"),
          ZRATE: node.querySelector("ZRATE")?.textContent || "",
          ZDISC: node.querySelector("ZDISC")?.textContent || "",
          ZPDISC: node.querySelector("ZPDISC")?.textContent || "",
          ZPVAT: node.querySelector("ZPVAT")?.textContent || "",
          ZVAT: node.querySelector("ZVAT")?.textContent || "",
          ZTAMT: node.querySelector("ZTAMT")?.textContent || "",
        }));

        // Combine head with each item
        const combinedEntries = items.map(item => ({
          ...head,
          ...item,
        }));

        // Add to results
        results.push(...combinedEntries);
      } catch (err) {
        if (err.response && err.response.status === 400) {
          errorMessages.push(
            err.response.data.message || "Invalid request"
          );
        } else {
          console.error(`Error fetching invoice ${invoice}:`, err);
          errorMessages.push(`Invoice ${invoice}: Failed to fetch data.`);
        }
      }
    }

    setData(results); // Save combined data into the state

    if (errorMessages?.length > 0) {
      setError(errorMessages.join(" | ")); // Combine error messages into a single string
    }
  } finally {
    setLoading(false);
  }
};



  return (
    <div className="flex flex-col bg-gray-50">
      <div className="container mx-auto p-6">

        {/* Input Section */}
        <div className="flex flex-col items-center gap-6">
          <div className="flex items-center gap-4 w-full max-w-md">
            <input
              type="text"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              placeholder="Enter Invoice Number"
              className="flex-grow border border-gray-300 p-3 rounded-md shadow-sm focus:ring focus:ring-blue-200"
            />
            <button
              onClick={handleAddInvoice}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:ring focus:ring-blue-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={invoices?.length >= 10}
            >
              Add
            </button>
          </div>
          {validationError && (
            <div className="text-red-600 text-sm font-medium mt-2">
              {validationError}
            </div>
          )}

          {/* Invoice List with Flex Wrap */}
          <div className="flex flex-wrap gap-4 w-full justify-center">
            {invoices.map((invoice, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-white shadow-md rounded-lg p-3 border border-gray-200 w-60"
              >
                <span className="text-gray-800 font-medium truncate">{invoice}</span>
                <button
                  onClick={() => handleRemoveInvoice(index)}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white focus:outline-none shadow-md"
                  aria-label="Remove Invoice"
                >
                  ✖
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Fetch Data */}
        <div className="mt-8 text-center">
          <button
            onClick={fetchDetails}
            className={`${loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
              } text-white px-6 py-3 rounded-md focus:ring focus:ring-green-300`}
            disabled={loading || invoices?.length === 0}
          >
            {loading ? "Fetching..." : "Fetch Details"}
          </button>
        </div>

        {data?.length === 0 && (
          <div className="mt-8 flex items-center justify-center mx-auto w-full p-6 border-2 border-dashed border-gray-300 rounded-md bg-yellow-50 ">
          <div className="text-left">
            <h2 className="text-xl font-medium text-gray-700 text-center">Follow these steps:</h2>
            <p className="text-gray-700 mt-2">
              <strong>Step 1:</strong> Enter the invoice number in the designated field.
            </p>
            <p className="text-gray-700 mt-1">
              <strong>Step 2:</strong> Click the "Add" button and repeat the process to include additional invoice numbers as needed.
            </p>
            <p className="text-gray-700 mt-1">
              <strong>Step 3:</strong> Click "Fetch Details" to retrieve the invoice information.
            </p>
          </div>
          </div>
        )}


        {/* Error Message */}
     <ErrorMessage error={error} show={error} />

      </div>

      {/* Table */}
      <div className="flex-grow overflow-y-auto">
        {data?.length > 0 && <InvoiceTable setData={setData} data={data} />}
      </div>
    </div>
  );
};

export default InvoicePage;
